/** For an AI diagnose audit row (`*.ai_diagnose.requested`) — or a console
 *  agent message (`console_agent.requested`, backend metaplan A9, same payload
 *  contract): a link to the chat and what the run — and its whole thread — cost. The backend appends
 *  `outcome` when the run ends and keeps `thread_total` current on the
 *  thread's first row (DiagnoseAuditPayload in the console spec). */
import { Link, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import type { components } from '@/api/types';

export type DiagnoseAuditPayload = components['schemas']['DiagnoseAuditPayload'];

const money = (usd: number | undefined) => (usd == null ? '' : `$${usd.toFixed(2)}`);
const questions = (n: number | undefined) => (n == null ? '' : ` (${n} question${n === 1 ? '' : 's'})`);

/** `this question $0.84 · completed · thread total $3.12 (4 questions)`, or
 *  null when the payload carries no cost information at all. */
export function diagnoseCostLine(p: DiagnoseAuditPayload): string | null {
  const parts: string[] = [];
  const o = p.outcome;
  if (o) {
    parts.push(`this question ${money(o.cost_usd)}`.trim());
    if (o.state) parts.push(o.state);
    if (o.budget_exhausted) parts.push('budget exhausted');
    if (o.refusal) parts.push('model refused');
  }
  // The first row of a thread keeps the live total; later rows carry the
  // total as it stood when they finished.
  const total = p.thread_total ?? o?.thread;
  if (total?.cost_usd != null) parts.push(`thread total ${money(total.cost_usd)}${questions(total.turns)}`);
  if (parts.length === 0) return null;
  return parts.join(' · ');
}

/** The audit events whose payload is a `DiagnoseAuditPayload`. */
export const CONSOLE_AGENT_EVENT = 'console_agent.requested';

export function isDiagnoseEvent(event: string): boolean {
  return event.endsWith('.ai_diagnose.requested') || event === CONSOLE_AGENT_EVENT;
}

/** Does this payload look like a diagnose request row? */
export function isDiagnosePayload(p: Record<string, unknown> | null | undefined): p is DiagnoseAuditPayload {
  return p != null && (typeof p.thread_path === 'string' || (p.outcome != null && typeof p.outcome === 'object'));
}

/** A console route — same origin only. `//host/x` starts with "/" too but is a
 *  protocol-relative URL the router would render as an off-site link. */
export function consolePath(value: unknown): string | null {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) {
    return null;
  }
  return value;
}

export function DiagnoseAuditSummary({
  payload,
  event = '',
}: {
  payload: DiagnoseAuditPayload;
  /** The row's event: a console agent message links to its conversation. */
  event?: string;
}) {
  const line = diagnoseCostLine(payload);
  const path = consolePath(payload.thread_path);
  const agent = event === CONSOLE_AGENT_EVENT;
  return (
    <>
      {path && (
        <Typography variant="body2">
          {agent ? 'AI agent' : 'AI diagnosis'}:{' '}
          <Link component={RouterLink} to={path}>
            {agent ? 'Open the conversation' : 'Open diagnosis thread'}
          </Link>
        </Typography>
      )}
      <Typography variant="body2" color={line ? 'text.primary' : 'text.secondary'}>
        Cost:{' '}
        {line ??
          (payload.preview
            ? 'context preview — no model call'
            : 'not recorded yet (still running, or the worker stopped before it could record)')}
      </Typography>
    </>
  );
}
